import React, { useState, useMemo } from 'react';
import { DollarSign, Search, CheckCircle, ChevronDown, ChevronUp, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import { formatIdentificationDisplay, formatIdentificationInput } from '../utils/formatIdentification';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getCalendarDays(viewDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));
  const remainder = days.length % 7;
  if (remainder) for (let i = 0; i < 7 - remainder; i++) days.push(null);
  return days;
}

const DividendPaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchIc, setSearchIc] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const [expandedEnrollmentIds, setExpandedEnrollmentIds] = useState(() => new Set());
  const [paidModal, setPaidModal] = useState(null);
  const [paidModalViewMonth, setPaidModalViewMonth] = useState(() => new Date());
  const [paidModalSelectedDate, setPaidModalSelectedDate] = useState(null);

  // Group payments by enrollment
  const paymentsByEnrollment = useMemo(() => {
    const map = new Map();
    for (const p of payments) {
      const id = p.enrollmentId;
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(p);
    }
    return map;
  }, [payments]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(amount);

  const formatDate = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' })
      : '—';

  const fetchPayments = async (icOverride) => {
    try {
      setLoading(true);
      setError('');
      const ic = icOverride !== undefined ? icOverride : searchIc;
      const params = new URLSearchParams();
      if ((ic || '').trim()) params.set('icNumber', (ic || '').trim());
      const res = await fetch(`${getApiUrl()}/api/dividend-payments${params.toString() ? `?${params}` : ''}`, {
        headers: getAuthHeaders(),
      });
      const contentType = res.headers.get('content-type') || '';
      let result;
      if (contentType.includes('application/json')) {
        result = await res.json();
      } else {
        const text = await res.text();
        console.error('Dividend payments API returned non-JSON:', res.status, text.slice(0, 200));
        setError(res.status === 404
          ? 'Dividend payments API not found. Please restart the backend server (npm start in backend folder).'
          : 'Unable to load dividend payments. Server returned an error.');
        setPayments([]);
        return;
      }
      if (result.success) setPayments(result.data || []);
      else setError(result.message || 'Failed to load dividend payments');
    } catch (err) {
      console.error('Fetch dividend payments error:', err);
      setError('Unable to load dividend payments. Check that the backend is running on ' + (getApiUrl() || 'the correct URL') + '.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setHasSearched(true);
    fetchPayments();
  };

  const openPaidModal = (payment) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setPaidModal({
      id: payment.id,
      dueDate: payment.dueDate,
      amount: payment.amount,
    });
    setPaidModalViewMonth(today);
    setPaidModalSelectedDate(today);
  };

  const handleMarkPaid = async (id, paidDate) => {
    try {
      setMarkingId(id);
      setError('');
      setSuccess('');
      const body = paidDate ? { paidDate: paidDate.toISOString().split('T')[0] } : {};
      const res = await fetch(`${getApiUrl()}/api/dividend-payments/${id}/paid`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: Object.keys(body).length ? JSON.stringify(body) : undefined,
      });
      const result = await res.json();
      if (result.success) {
        setSuccess('Marked as paid.');
        setPaidModal(null);
        fetchPayments();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Failed to mark as paid');
      }
    } catch (err) {
      console.error('Mark paid error:', err);
      setError('Failed to update');
    } finally {
      setMarkingId(null);
    }
  };

  const confirmPaidDate = () => {
    if (!paidModal || !paidModalSelectedDate) return;
    handleMarkPaid(paidModal.id, paidModalSelectedDate);
  };

  const isSameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const isToday = (d) => d && isSameDay(d, new Date());

  return (
    <div className="space-y-4">
      <p className="text-gray-600 dark:text-dark-muted">
        Search investor dividend payments by IC. Enter an IC number and click Search to view and mark payments as paid.
      </p>

      {/* Search by IC */}
      <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-3">
        <div className="min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
            Investors' Identification Number
          </label>
          <input
            type="text"
            value={searchIc}
            onChange={(e) => setSearchIc(formatIdentificationInput(e.target.value))}
            // placeholder="Identification Number"
            className="w-full neo-field dark:text-dark-text px-3 py-2 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="flex items-center gap-2 px-4 py-2 neo-btn-primary"
        >
          <Search size={18} />
          Search
        </button>
        {hasSearched && (
          <button
            type="button"
            onClick={() => {
              setSearchIc('');
              setHasSearched(false);
              setPayments([]);
              setError('');
            }}
            className="px-4 py-2 neo-btn text-gray-700 dark:text-dark-text"
          >
            Clear
          </button>
        )}
      </form>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-sm">
          <CheckCircle size={18} />
          {success}
        </div>
      )}

      {/* Table */}
      <div className="neo-raised overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
          </div>
        ) : !hasSearched ? (
          <div className="p-8 text-center text-gray-500 dark:text-dark-muted">
            Search by Investors' Identification Number to View Dividend Payments.
          </div>
        ) : payments.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-dark-muted">
            No dividend payments found. {searchIc ? 'Try a different IC or clear to search again.' : 'No records found.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-dark-border">
            {Array.from(paymentsByEnrollment.entries()).map(([enrollmentId, groupPayments]) => {
              const first = groupPayments[0];
              const inv = first.enrollment?.investor;
              const product = first.enrollment?.productDetails?.product;
              const enrollmentAmount = formatCurrency(first.enrollment?.amount ?? 0);
              const isExpanded = expandedEnrollmentIds.has(enrollmentId);
              const pendingCount = groupPayments.filter((p) => p.status !== 'Paid').length;
              const paidCount = groupPayments.length - pendingCount;

              return (
                <div key={enrollmentId} className="border-b border-gray-200 dark:border-dark-border last:border-b-0">
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedEnrollmentIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(enrollmentId)) next.delete(enrollmentId);
                        else next.add(enrollmentId);
                        return next;
                      });
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 dark:bg-slate-800/50 hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span className="font-medium text-gray-900 dark:text-dark-text">{inv?.name ?? '—'}</span>
                      <span className="text-gray-500 dark:text-dark-muted">
                        {inv?.icNumber ? formatIdentificationDisplay(inv.icNumber) : ''}
                      </span>
                      <span className="text-gray-600 dark:text-dark-text">{product?.name ?? '—'}</span>
                      <span className="font-medium text-gray-900 dark:text-dark-text">{enrollmentAmount}</span>
                      <span className="text-xs text-gray-500 dark:text-dark-muted">
                        {groupPayments.length} payment{groupPayments.length !== 1 ? 's' : ''}
                        {pendingCount > 0 && ` · ${pendingCount} pending`}
                        {paidCount > 0 && ` · ${paidCount} paid`}
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="text-gray-400 flex-shrink-0" size={20} />
                    ) : (
                      <ChevronDown className="text-gray-400 flex-shrink-0" size={20} />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-dark-border">
                          <tr>
                            <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">Year</th>
                            <th className="text-center px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">#</th>
                            <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">Due date</th>
                            <th className="text-right px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">Amount</th>
                            <th className="text-center px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">Status</th>
                            <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">Paid date</th>
                            <th className="text-center px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
                          {groupPayments.map((p) => (
                            <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30">
                              <td className="px-4 py-2 text-gray-900 dark:text-dark-text">Year {p.year}</td>
                              <td className="px-4 py-2 text-center text-gray-900 dark:text-dark-text">{p.dividendNumber}</td>
                              <td className="px-4 py-2 text-gray-900 dark:text-dark-text">{formatDate(p.dueDate)}</td>
                              <td className="px-4 py-2 text-right text-gray-900 dark:text-dark-text">{formatCurrency(p.amount)}</td>
                              <td className="px-4 py-2 text-center">
                                <span
                                  className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                    p.status === 'Paid'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                      : p.status === 'Overdue'
                                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  }`}
                                >
                                  {p.status}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-gray-900 dark:text-dark-text">{formatDate(p.paidDate)}</td>
                              <td className="px-4 py-2 text-center">
                                {p.status !== 'Paid' ? (
                                  <button
                                    type="button"
                                    onClick={() => openPaidModal(p)}
                                    disabled={markingId === p.id}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 disabled:opacity-50"
                                  >
                                    {markingId === p.id ? (
                                      <>...</>
                                    ) : (
                                      <>
                                        <DollarSign size={14} />
                                        Paid
                                      </>
                                    )}
                                  </button>
                                ) : (
                                  <span className="text-gray-400 dark:text-dark-muted text-xs">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Select paid date modal with styled calendar */}
      {paidModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setPaidModal(null)}>
          <div
            className="neo-modal w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-dark-border">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">Select paid date</h3>
              <button
                type="button"
                onClick={() => setPaidModal(null)}
                className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-dark-muted dark:hover:text-dark-text dark:hover:bg-slate-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={() =>
                    setPaidModalViewMonth((d) => {
                      const next = new Date(d);
                      next.setMonth(next.getMonth() - 1);
                      return next;
                    })
                  }
                  className="p-2 rounded-lg text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-slate-700"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="text-sm font-medium text-gray-900 dark:text-dark-text">
                  {MONTHS[paidModalViewMonth.getMonth()]} {paidModalViewMonth.getFullYear()}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setPaidModalViewMonth((d) => {
                      const next = new Date(d);
                      next.setMonth(next.getMonth() + 1);
                      return next;
                    })
                  }
                  className="p-2 rounded-lg text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-slate-700"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-0.5 mb-2">
                {WEEKDAYS.map((wd) => (
                  <div key={wd} className="text-center text-xs font-medium text-gray-500 dark:text-dark-muted py-1">
                    {wd}
                  </div>
                ))}
                {getCalendarDays(paidModalViewMonth).map((day, idx) => {
                  if (!day) {
                    return <div key={`e-${idx}`} className="aspect-square" />;
                  }
                  const selected = isSameDay(day, paidModalSelectedDate);
                  const today = isToday(day);
                  const inViewMonth = day.getMonth() === paidModalViewMonth.getMonth();
                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      onClick={() => setPaidModalSelectedDate(day)}
                      className={`
                        aspect-square rounded-lg text-sm font-medium transition-colors
                        ${!inViewMonth ? 'text-gray-300 dark:text-slate-600' : 'text-gray-900 dark:text-dark-text'}
                        ${selected ? 'bg-primary-600 text-white hover:bg-primary-700' : 'hover:bg-gray-100 dark:hover:bg-slate-700'}
                        ${today && !selected ? 'ring-1 ring-primary-500 ring-inset' : ''}
                      `}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    const t = new Date();
                    t.setHours(0, 0, 0, 0);
                    setPaidModalSelectedDate(t);
                    setPaidModalViewMonth(t);
                  }}
                  className="flex-1 px-3 py-2 text-sm font-medium neo-btn text-gray-700 dark:text-dark-text"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => setPaidModal(null)}
                  className="flex-1 px-3 py-2 text-sm font-medium neo-btn text-gray-700 dark:text-dark-text"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmPaidDate}
                  disabled={!paidModalSelectedDate || markingId === paidModal.id}
                  className="flex-1 px-3 py-2 text-sm font-medium neo-btn-primary disabled:opacity-50"
                >
                  {markingId === paidModal.id ? '...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DividendPaymentManagement;
